import { type DependencyList, useCallback, useEffect, useState } from "react";

export interface Loaded<T> {
  readonly data: T | undefined;
  readonly error: string | undefined;
  readonly loading: boolean;
  /** Loads again. */
  readonly reload: () => void;
  /** Replaces the data, after a change the page made itself. */
  readonly set: (data: T) => void;
}

/** Runs `load` when the dependencies change; a late answer to an older call is dropped. */
export function useLoad<T>(load: () => Promise<T>, deps: DependencyList): Loaded<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(true);
  const [round, setRound] = useState(0);

  // The caller names what `load` reads in `deps`.
  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(undefined);
    load().then(
      (value) => {
        if (!current) return;
        setData(value);
        setLoading(false);
      },
      (reason: unknown) => {
        if (!current) return;
        setError(reason instanceof Error ? reason.message : String(reason));
        setLoading(false);
      },
    );
    return () => {
      current = false;
    };
  }, [...deps, round]);

  const reload = useCallback(() => setRound((n) => n + 1), []);
  return { data, error, loading, reload, set: setData };
}

/** The message of whatever was thrown. */
export function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
